/**
 * Persisted data of the 3D design extension (all OPTIONAL fields of the layout; older designs simply lack them).
 *
 *  layout.structures = { version: 1, items: StructureInstance[], sections: SteelSection[] }
 *  layout.buildings  = { version: 1, items: Building[] }                 (building.ts)
 *  layout.electrical = { version: 1, equipment: [...], routes: [...] }
 *
 * Unknown fields are kept (`.loose()`) so a newer tool version never loses data saved by an older one and vice versa.
 * Coordinates: canvas px for anything drawn on the 2D plan; structure-local metres for user-added members.
 */
import { z } from 'zod';
import { MEMBER_ROLES, STRUCTURE_KINDS } from './kinds';
import { mountingRestrictionSchema } from './mounting-config';
import { steelSectionSchema } from './sections';

export const STRUCTURE_DATA_VERSION = 1;
/** Bumped when generators change geometry for identical parameters. */
export const STRUCTURE_ENGINE_VERSION = 1;

const v3 = z.tuple([z.number().finite(), z.number().finite(), z.number().finite()]);
const pt = z.tuple([z.number().finite(), z.number().finite()]);
const id = z.string().min(1).max(40);
const paramValue = z.union([z.number().finite(), z.string().max(40), z.boolean()]);

export const memberOverrideSchema = z
  .object({
    sectionId: z.string().max(60).optional(),
    removed: z.boolean().optional(),
    /** Extend (+) or trim (−) the member at its start / end, mm */
    extendStartMm: z.number().min(-20_000).max(20_000).optional(),
    extendEndMm: z.number().min(-20_000).max(20_000).optional(),
    rollDeg: z.number().min(-180).max(180).optional(),
  })
  .loose();
export type MemberOverride = z.infer<typeof memberOverrideSchema>;

/** Member added by the user, in structure-local metres (x along rows, y towards the back, z up). */
export const addedMemberSchema = z
  .object({
    id,
    role: z.enum(MEMBER_ROLES),
    sectionId: z.string().max(60),
    a: v3,
    b: v3,
    rollDeg: z.number().min(-180).max(180).optional(),
    label: z.string().max(80).optional(),
  })
  .loose();
export type AddedMember = z.infer<typeof addedMemberSchema>;

export const structureInstanceSchema = z
  .object({
    id,
    name: z.string().trim().min(1).max(120),
    kind: z.enum(STRUCTURE_KINDS),
    templateId: z.string().max(60),
    templateRevision: z.number().int().min(1).max(1e6).default(1),
    /** Roof polygon carrying the structure: stable uid, with the index as a fallback for designs without uids */
    roofUid: z.string().max(40).nullable().default(null),
    roofIndex: z.number().int().min(0).max(10_000).nullable().default(null),
    /** Installation zone of the roof whose panels the structure carries (design tool); absent ⇒ every panel of the roof */
    zoneId: z.string().max(40).nullable().optional(),
    params: z.record(z.string().max(40), paramValue).default({}),
    sections: z.record(z.string().max(20), z.string().max(60)).default({}),
    overrides: z.record(z.string().max(40), memberOverrideSchema).default({}),
    added: z.array(addedMemberSchema).max(1000).default([]),
    hidden: z.boolean().optional(),
    /** Mounting configurations allowed by the selected product's documentation (entered by the engineer) */
    mounting: mountingRestrictionSchema.optional(),
  })
  .loose();
export type StructureInstance = z.infer<typeof structureInstanceSchema>;

export const structuresDataSchema = z
  .object({
    version: z.number().int().min(1),
    items: z.array(structureInstanceSchema).max(200),
    /** Project-specific sections (catalogue / user); built-ins are not stored */
    sections: z.array(steelSectionSchema).max(300).default([]),
  })
  .loose();
export type StructuresData = z.infer<typeof structuresDataSchema>;

// ---------------------------------------------------------------- electrical (representation only)

export const EQUIPMENT_KINDS = ['INVERTER', 'CABINET', 'COMBINER', 'BATTERY', 'METER', 'OTHER'] as const;
export type EquipmentKind = (typeof EQUIPMENT_KINDS)[number];
export const EQUIPMENT_KIND_LABEL: Record<EquipmentKind, string> = {
  INVERTER: 'Biến tần',
  CABINET: 'Tủ điện',
  COMBINER: 'Hộp gom DC',
  BATTERY: 'Pin lưu trữ',
  METER: 'Công tơ / đồng hồ',
  OTHER: 'Thiết bị khác',
};
export const CABLE_ROUTE_KINDS = ['TRAY', 'DC', 'AC'] as const;
export type CableRouteKind = (typeof CABLE_ROUTE_KINDS)[number];
export const CABLE_ROUTE_LABEL: Record<CableRouteKind, string> = {
  TRAY: 'Máng cáp',
  DC: 'Tuyến cáp DC',
  AC: 'Tuyến cáp AC',
};

export const equipmentSchema = z
  .object({
    id,
    kind: z.enum(EQUIPMENT_KINDS),
    label: z.string().trim().min(1).max(80),
    /** Catalogue product (links the 3D object to the engineering BOQ equipment) */
    productId: z.uuid().nullable().default(null),
    /** Plan position on the canvas (px) */
    at: pt,
    mount: z.enum(['ROOF', 'GROUND']).default('GROUND'),
    roofUid: z.string().max(40).nullable().default(null),
    /** Base elevation above the roof / ground, mm */
    elevationMm: z.number().min(0).max(30_000).default(0),
    sizeMm: z.tuple([
      z.number().min(10).max(20_000),
      z.number().min(10).max(20_000),
      z.number().min(10).max(20_000),
    ]),
    rotationDeg: z.number().min(-360).max(360).default(0),
    /** Explicit connections (never inferred from proximity) */
    connectsTo: z.array(z.string().max(40)).max(50).default([]),
  })
  .loose();
export type EquipmentInstance = z.infer<typeof equipmentSchema>;

export const cableRouteSchema = z
  .object({
    id,
    kind: z.enum(CABLE_ROUTE_KINDS),
    label: z.string().max(80).optional(),
    points: z.array(pt).min(2).max(500),
    mount: z.enum(['ROOF', 'GROUND']).default('ROOF'),
    /** Height above the surface below, mm */
    elevationMm: z.number().min(0).max(30_000).default(150),
    widthMm: z.number().min(10).max(2000).default(100),
    heightMm: z.number().min(10).max(1000).default(50),
    fromId: z.string().max(40).nullable().default(null),
    toId: z.string().max(40).nullable().default(null),
  })
  .loose();
export type CableRoute = z.infer<typeof cableRouteSchema>;

export const electricalDataSchema = z
  .object({
    version: z.number().int().min(1),
    equipment: z.array(equipmentSchema).max(500).default([]),
    routes: z.array(cableRouteSchema).max(500).default([]),
  })
  .loose();
export type ElectricalData = z.infer<typeof electricalDataSchema>;

// ---------------------------------------------------------------- templates

export const TEMPLATE_SCHEMA = 'S.M.A.R.T.structure-template';
export const TEMPLATE_VERSION = 1;

export const TEMPLATE_SEGMENTS = ['RESIDENTIAL', 'CI', 'BOTH'] as const;
export const TEMPLATE_ROOF_TYPES = ['PITCHED', 'FLAT', 'METAL', 'GROUND'] as const;
export const TEMPLATE_MOUNTINGS = ['ROOF_PARALLEL', 'TILTED', 'ELEVATED', 'CARPORT'] as const;
export const TEMPLATE_CLEARANCES = ['LOW', 'MEDIUM', 'HIGH'] as const;
export const TEMPLATE_ROWS = ['SINGLE', 'MULTI'] as const;
export const TEMPLATE_PROFILES = ['STANDARD', 'CUSTOM'] as const;

export const TEMPLATE_CATEGORY_LABEL = {
  segment: { RESIDENTIAL: 'Dân dụng', CI: 'C&I', BOTH: 'Dân dụng & C&I' },
  roofTypes: { PITCHED: 'Mái dốc', FLAT: 'Mái bằng', METAL: 'Mái tôn', GROUND: 'Mặt đất (độc lập)' },
  mounting: { ROOF_PARALLEL: 'Song song mái', TILTED: 'Khung nghiêng', ELEVATED: 'Khung nâng', CARPORT: 'Nhà để xe' },
  clearance: { LOW: 'Thấp', MEDIUM: 'Trung bình', HIGH: 'Thông thuỷ cao' },
  rows: { SINGLE: 'Một hàng', MULTI: 'Nhiều hàng' },
  profile: { STANDARD: 'Thép hình tiêu chuẩn', CUSTOM: 'Kết cấu tuỳ chỉnh' },
} as const;

export const templateCategoriesSchema = z.object({
  segment: z.enum(TEMPLATE_SEGMENTS),
  roofTypes: z.array(z.enum(TEMPLATE_ROOF_TYPES)).min(1).max(3),
  mounting: z.enum(TEMPLATE_MOUNTINGS),
  clearance: z.enum(TEMPLATE_CLEARANCES),
  rows: z.enum(TEMPLATE_ROWS),
  profile: z.enum(TEMPLATE_PROFILES),
});

export const structureTemplateSchema = z
  .object({
    schema: z.literal(TEMPLATE_SCHEMA),
    version: z.literal(TEMPLATE_VERSION),
    id: z
      .string()
      .min(1)
      .max(60)
      .regex(/^[A-Za-z0-9_-]+$/),
    name: z.string().trim().min(1, 'Nhập tên mẫu').max(120),
    description: z.string().max(1000).default(''),
    kind: z.enum(STRUCTURE_KINDS),
    /** Content revision of this template (incremented on every save) */
    revision: z.number().int().min(1).default(1),
    /** Generator version the template was saved with (compatibility check) */
    engineVersion: z.number().int().min(1).default(STRUCTURE_ENGINE_VERSION),
    categories: templateCategoriesSchema,
    params: z.record(z.string().max(40), paramValue).default({}),
    sections: z.record(z.string().max(20), z.string().max(60)).default({}),
    overrides: z.record(z.string().max(40), memberOverrideSchema).default({}),
    added: z.array(addedMemberSchema).max(1000).default([]),
    /** Non built-in sections referenced by the template */
    sectionDefs: z.array(steelSectionSchema).max(100).default([]),
    /** PV mounting interface (documentation + clamp-zone check) */
    mounting: mountingRestrictionSchema.optional(),
    pvInterface: z
      .object({
        attach: z.enum(['RAILS', 'MINI_RAILS', 'PURLINS']),
        supportLinesPerRow: z.number().int().min(1).max(10),
        clampZonePct: z
          .tuple([z.number().min(0).max(50), z.number().min(0).max(50)])
          .nullable()
          .default(null),
      })
      .optional(),
    /** Member definitions & connection relationships captured when the template was saved (documentation) */
    memberSummary: z
      .array(
        z.object({
          role: z.enum(MEMBER_ROLES),
          sectionId: z.string().max(60),
          count: z.number().int().min(0),
        }),
      )
      .max(50)
      .default([]),
    connections: z
      .array(
        z.object({ from: z.enum(MEMBER_ROLES), to: z.enum(MEMBER_ROLES), count: z.number().int().min(0) }),
      )
      .max(100)
      .default([]),
    builtIn: z.boolean().default(false),
    createdAt: z.string().max(40).optional(),
    updatedAt: z.string().max(40).optional(),
    createdBy: z.string().max(150).optional(),
  })
  .loose();
export type StructureTemplate = z.infer<typeof structureTemplateSchema>;

export type MigrateResult<T> = { ok: true; value: T; migrated: boolean } | { ok: false; error: string };

/**
 * Explicit, ordered migrations for persisted structure data. There is only version 1 today; future versions add an
 * entry `n: (data) => data_(n+1)` here instead of changing how version-1 files are read.
 */
const STRUCTURE_MIGRATIONS: Record<number, (raw: Record<string, unknown>) => Record<string, unknown>> = {};

export function migrateStructures(raw: unknown): MigrateResult<StructuresData> {
  if (!raw || typeof raw !== 'object') return { ok: false, error: 'Dữ liệu kết cấu không hợp lệ.' };
  let o = raw as Record<string, unknown>;
  let v = typeof o.version === 'number' ? o.version : 1;
  const from = v;
  if (v > STRUCTURE_DATA_VERSION)
    return {
      ok: false,
      error: `Dữ liệu kết cấu phiên bản ${v} mới hơn ứng dụng (hỗ trợ ${STRUCTURE_DATA_VERSION}).`,
    };
  while (v < STRUCTURE_DATA_VERSION) {
    const step = STRUCTURE_MIGRATIONS[v];
    if (!step) return { ok: false, error: `Không có bước chuyển đổi dữ liệu kết cấu từ phiên bản ${v}.` };
    o = step(o);
    v++;
  }
  const r = structuresDataSchema.safeParse({ ...o, version: v });
  if (!r.success)
    return { ok: false, error: `Dữ liệu kết cấu không hợp lệ: ${r.error.issues[0]?.message ?? ''}` };
  return { ok: true, value: r.data, migrated: from !== v };
}

const TEMPLATE_MIGRATIONS: Record<number, (raw: Record<string, unknown>) => Record<string, unknown>> = {};

/** Read a template from an untrusted source (file import, API, localStorage). */
export function migrateTemplate(raw: unknown): MigrateResult<StructureTemplate> {
  let o: unknown = raw;
  if (typeof raw === 'string') {
    try {
      o = JSON.parse(raw);
    } catch {
      return { ok: false, error: 'Tệp mẫu không phải JSON hợp lệ.' };
    }
  }
  if (!o || typeof o !== 'object') return { ok: false, error: 'Mẫu kết cấu rỗng hoặc sai định dạng.' };
  let t = o as Record<string, unknown>;
  if (t.schema !== TEMPLATE_SCHEMA)
    return { ok: false, error: 'Không phải tệp mẫu kết cấu (schema không khớp).' };
  let v = typeof t.version === 'number' ? t.version : 0;
  const from = v;
  if (v > TEMPLATE_VERSION)
    return { ok: false, error: `Mẫu phiên bản ${v} mới hơn ứng dụng (hỗ trợ ${TEMPLATE_VERSION}).` };
  while (v < TEMPLATE_VERSION) {
    const step = TEMPLATE_MIGRATIONS[v];
    if (!step) return { ok: false, error: `Không có bước chuyển đổi mẫu từ phiên bản ${v}.` };
    t = step(t);
    v++;
  }
  const r = structureTemplateSchema.safeParse({ ...t, version: v });
  if (!r.success) {
    const i = r.error.issues[0];
    return {
      ok: false,
      error: `Mẫu kết cấu không hợp lệ: ${i?.path.join('.') ?? ''} ${i?.message ?? ''}`.trim(),
    };
  }
  if (r.data.engineVersion > STRUCTURE_ENGINE_VERSION)
    return { ok: false, error: 'Mẫu được tạo bởi bộ dựng kết cấu mới hơn — hãy cập nhật ứng dụng.' };
  return { ok: true, value: r.data, migrated: from !== v };
}
